import type React from "react";
import { useEffect } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "./routes";

const faqData = [
  {
    question: "What is MycenAI?",
    answer: "MycenAI is a secure, AI-powered tool built specifically for mental health clinicians. It helps generate clinical notes (SOAP, BIRP, DAP, progress notes), treatment plans, SMART goals, assessments, supervision reflections, and client psychoeducation materials — all while keeping client information completely confidential.",
  },
  {
    question: "How does MycenAI protect client confidentiality?",
    answer: "Your session content is processed inside hardware-secured Trusted Execution Environments (TEEs), where data is isolated in encrypted memory during AI inference. MycenAI does not store clinical content on its own servers — conversation history resides within NEAR AI's TEE-protected infrastructure. We retain only non-sensitive metadata (IDs, timestamps) on our infrastructure. Cryptographic attestation verifies that processing occurs in a genuine secure enclave.",
  },
  {
    question: "How is it different from other AI note-taking tools?",
    answer: "Most AI platforms process your data on standard cloud servers where the provider has full access. MycenAI uses NEAR AI's hardware-secured Trusted Execution Environments, where data is protected by Intel TDX during AI processing — verified by cryptographic attestation, not just promises. We don't store clinical content on our servers, we don't monetize your data, and your subscription funds the service.",
  },
  {
    question: "What clinical features does it offer?",
    answer: "20 evidence-based templates adapted to major modalities (CBT, DBT, ACT, psychodynamic, person-centered, narrative, solution-focused, and more). Includes documentation (SOAP/BIRP/DAP notes, treatment summaries), planning (modality-specific interventions, SMART goals), assessment (intake reports, mental status exams, risk screens), supervision (reflective prompts, countertransference, ethical case questions), and client resources (plain-language psychoeducation, exposure hierarchies, values worksheets).",
  },
  {
    question: "Can I use multiple modalities or a blended approach?",
    answer: "Yes. Most experienced therapists don't work within a single rigid modality, and MycenAI is designed to reflect that reality. You can select multiple therapeutic modalities, and the AI will integrate concepts, language, and interventions from each into your documentation. If you practise from an integrative or eclectic framework — blending CBT with psychodynamic concepts, drawing on ACT and narrative approaches in the same session, or adapting your approach to different clients — MycenAI adapts with you. The tool moulds to how you actually practise, rather than forcing your clinical thinking into a single-modality box.",
  },
  {
    question: "Which AI models power MycenAI?",
    answer: "Access to high-quality models (GLM-4 default), all processed with the same strong privacy protections.",
  },
  {
    question: "Is MycenAI HIPAA compliant?",
    answer: "Designed for HIPAA alignment from the start, with TEE-protected AI processing and no clinical content stored on MycenAI's infrastructure.",
  },
  {
    question: "Can I back up or transfer my chats?",
    answer: "Yes — we strongly encourage this. Because we don't store your clinical content on our servers, we cannot retrieve it for you. MycenAI provides straightforward tools to download and back up your conversations (JSON, TXT, PDF formats). You maintain full control and ownership of your work.",
  },
  {
    question: "How much does it cost?",
    answer: "Beta access is free. Beta users will be eligible for exclusive founding member pricing when subscriptions launch. Pricing will remain affordable for independent practitioners. No data selling — your subscription supports the platform.",
  },
  {
    question: "How do I get started?",
    answer: "Visit https://mycenai.org and sign in with Google. Email support@mycenai.org with a brief note about your practice to request beta access. The interface is intuitive; work saves automatically and exports easily.",
  },
  {
    question: "Can I give feedback or suggest improvements?",
    answer: "Yes — your input directly shapes development. Email support@mycenai.org with suggestions for new prompts, modalities, or features.",
  },
];

const FAQPage: React.FC = () => {
  useEffect(() => {
    // Scroll the app's scroll container (not window) to top
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector(".overflow-y-auto");
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: "instant" });
      }
    });
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <Helmet>
        <title>FAQ — MycenAI</title>
        <meta
          name="description"
          content="Frequently asked questions about MycenAI's secure AI clinical documentation for mental health professionals."
        />
        <link rel="canonical" href="https://mycenai.org/faq/" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="mx-auto w-full max-w-3xl px-4 py-12">
        <Link
          to={APP_ROUTES.SPLASH}
          className="mb-8 inline-block text-muted-foreground text-sm hover:text-foreground"
        >
          ← Back
        </Link>

        <div className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="mb-6 font-bold text-3xl">MycenAI FAQ for Mental Health Professionals</h1>

          <h2 className="mt-8 mb-4 font-semibold text-xl">What is MycenAI?</h2>
          <p className="mb-4">
            MycenAI is a secure, AI-powered tool built specifically for mental health clinicians. It helps generate clinical notes (SOAP, BIRP, DAP, progress notes), treatment plans, SMART goals, assessments, supervision reflections, and client psychoeducation materials—all while keeping client information completely confidential.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">How does MycenAI protect client confidentiality?</h2>
          <p className="mb-4">
            Your session content is processed inside hardware-secured Trusted Execution Environments (TEEs), where data is isolated in encrypted memory during AI inference. MycenAI does not store clinical content on its own servers — conversation history resides within NEAR AI's TEE-protected infrastructure. We retain only non-sensitive metadata (IDs, timestamps) on our infrastructure. Cryptographic attestation verifies that processing occurs in a genuine secure enclave. This design aligns with HIPAA principles (BAA finalization underway). For full technical details, see our <Link to={APP_ROUTES.PRIVACY} className="text-primary hover:underline">Privacy Policy</Link>.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">How is it different from other AI note-taking tools?</h2>
          <p className="mb-4">
            Most AI platforms process your data on standard cloud servers where the provider has full access. MycenAI uses NEAR AI's hardware-secured Trusted Execution Environments, where data is protected by Intel TDX during AI processing — verified by cryptographic attestation, not just promises. We don't store clinical content on our servers, we don't monetize your data, and your subscription funds the service. This commitment is codified in our <Link to={APP_ROUTES.TERMS} className="text-primary hover:underline">Terms of Service</Link>.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">What clinical features does it offer?</h2>
          <p className="mb-4">
            20 evidence-based templates adapted to major modalities (CBT, DBT, ACT, psychodynamic, person-centered, narrative, solution-focused, and more). Examples include:
          </p>
          <ul className="mb-4 list-disc pl-5">
              <li>Documentation: <Link to="/blog/soap-vs-birp-vs-dap" className="text-primary hover:underline">SOAP/BIRP/DAP notes</Link>, treatment summaries</li>
              <li>Planning: Modality-specific interventions, SMART goals</li>
              <li>Assessment: Intake reports, mental status exams, risk screens</li>
              <li>Supervision: Reflective prompts, countertransference, ethical case questions</li>
              <li>Client resources: Plain-language psychoeducation, exposure hierarchies, values worksheets</li>
          </ul>
          <p className="mb-4">
            Prompts are grounded in peer-reviewed research with citations; they support—not replace—your clinical judgment.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Can I use multiple modalities or a blended approach?</h2>
          <p className="mb-4">
            Yes. Most experienced therapists don't work within a single rigid modality, and MycenAI is designed to reflect that reality. You can select multiple therapeutic modalities, and the AI will integrate concepts, language, and interventions from each into your documentation. If you practise from an integrative or eclectic framework — blending CBT with psychodynamic concepts, drawing on ACT and narrative approaches in the same session, or adapting your approach to different clients — MycenAI adapts with you. The tool moulds to how you actually practise, rather than forcing your clinical thinking into a single-modality box.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Which AI models power MycenAI?</h2>
          <p className="mb-4">
            Access to high-quality models (GLM-4 default), all processed with the same strong privacy protections.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Is MycenAI HIPAA compliant?</h2>
          <p className="mb-4">
            Designed for HIPAA alignment from the start, with TEE-protected AI processing and no clinical content stored on MycenAI's infrastructure. See our <Link to={APP_ROUTES.PRIVACY} className="text-primary hover:underline">Privacy Policy</Link> for details.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Can I back up or transfer my chats?</h2>
          <p className="mb-4">
            Yes—we strongly encourage this. Because we don't store your clinical content on our servers, we cannot retrieve it for you. MycenAI provides straightforward tools to download and back up your conversations (JSON, TXT, PDF formats). You maintain full control and ownership of your work—see our <Link to={APP_ROUTES.TERMS} className="text-primary hover:underline">Terms of Service</Link> for details on content ownership.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">How much does it cost?</h2>
          <p className="mb-4">
            Beta access is free. Beta users will be eligible for exclusive founding member pricing when subscriptions launch. Pricing will remain affordable for independent practitioners—we're building this for clinicians, not enterprise budgets. No data selling—your subscription supports the platform.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">How do I get started?</h2>
          <p className="mb-4">
            Visit <a href="https://mycenai.org" className="text-primary hover:underline">https://mycenai.org</a> and sign in with Google. Email <a href="mailto:support@mycenai.org" className="text-primary hover:underline">support@mycenai.org</a> with a brief note about your practice to request beta access. The interface is intuitive; work saves automatically and exports easily.
          </p>

           <h2 className="mt-8 mb-4 font-semibold text-xl">Can I give feedback or suggest improvements?</h2>
          <p className="mb-4">
            Yes—your input directly shapes development. Email <a href="mailto:support@mycenai.org" className="text-primary hover:underline">support@mycenai.org</a> with suggestions for new prompts, modalities, or features.
          </p>

          <hr className="my-8 border-border" />

          <p className="mb-4">
             MycenAI exists to help independent practitioners reclaim time for what matters most: your clients—without compromising confidentiality or ethics.
          </p>

          <p className="font-semibold">
              Questions? Reach out at <a href="mailto:support@mycenai.org" className="text-primary hover:underline">support@mycenai.org</a>
          </p>
          <p className="mt-2 text-muted-foreground text-sm">
             Built with care for the mental health community.
          </p>

        </div>
      </div>
    </div>
  );
};

export default FAQPage;
